package listening

import UserProgress
import androidx.compose.runtime.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.collections.filter

@Composable
fun ListeningMenuScreen(
    onTaskSelected: (Int) -> Unit,
    onBack: () -> Unit
) {
    var userProgress by remember { mutableStateOf<List<UserProgress>>(emptyList()) }

    LaunchedEffect(Unit) {
        // Загружаем весь прогресс
        userProgress = DataRepository.getMyProgress()
    }

    // --- СЧИТАЕМ СТАТИСТИКУ (Только для Аудирования, ID > 10) ---
    // В базе данных Tarea 1 Auditiva = 11, Tarea 2 = 12 и т.д.
    val listeningProgress = userProgress.filter { it.taskId > 10 }

    val totalSetsSolved = listeningProgress.size
    val totalCorrect = listeningProgress.sumOf { it.score }
    val totalQuestions = listeningProgress.sumOf { it.totalQuestions }

    // Средняя точность (защита от деления на 0)
    val accuracy = if (totalQuestions > 0) (totalCorrect * 100 / totalQuestions) else 0

    // Уровень пользователя (Геймификация для Аудирования)
    val userLevel = when {
        totalSetsSolved >= 20 -> "Nativo 🎧"
        totalSetsSolved >= 10 -> "Fluido 🗣️"
        totalSetsSolved >= 5 -> "Intermedio 👂"
        totalSetsSolved > 0 -> "Principiante 🌱"
        else -> "Nuevo usuario"
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            padding(20.px)
            // Светло-оранжевый фон, чтобы визуально отличаться от Чтения
            backgroundColor(Color("#fffbf0"))
            minHeight(100.vh)
        }
    }) {
        // --- ШАПКА ---
        Div({
            style {
                width(100.percent)
                maxWidth(800.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.FlexStart)
                alignItems(AlignItems.Center)
                marginBottom(20.px)
                property("gap", "20px")
            }
        }) {
            Button({
                style {
                    backgroundColor(Color.white); border(0.px); fontSize(1.5.cssRem)
                    cursor("pointer"); padding(5.px, 15.px); borderRadius(20.px)
                    property("box-shadow", "0 2px 5px rgba(0,0,0,0.1)")
                }
                onClick { onBack() }
            }) { Text("⬅️") }

            H2({ style { margin(0.px); color(Color("#d35400")) } }) {
                Text("Comprensión Auditiva")
            }
        }

        // --- ИНФОРМАЦИОННАЯ ПАНЕЛЬ (DASHBOARD) ---
        Div({
            style {
                backgroundColor(Color.white)
                width(100.percent)
                maxWidth(800.px)
                borderRadius(16.px)
                padding(25.px)
                marginBottom(30.px)
                property("box-shadow", "0 4px 15px rgba(0,0,0,0.05)")
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceAround)
                property("flex-wrap", "wrap")
                property("gap", "20px")
            }
        }) {
            // Стат 1: Уровень
            ListeningStatItem(icon = "🎖️", label = "Nivel", value = userLevel)

            // Стат 2: Решено сетов
            ListeningStatItem(icon = "🎧", label = "Audios resueltos", value = "$totalSetsSolved")

            // Стат 3: Точность
            ListeningStatItem(icon = "⚡", label = "Precisión", value = "$accuracy%", valueColor = if(accuracy > 70) "#27ae60" else "#e67e22")
        }

        // --- СПИСОК ЗАДАЧ (GRID) ---
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fill, minmax(300px, 1fr))")
                property("gap", "20px")
                width(100.percent)
                maxWidth(800.px)
            }
        }) {
            // Генерируем 5 карточек (Tarea 1-5)
            for (i in 1..5) {
                // Вычисляем реальный ID в базе (11, 12, 13...)
                val dbTaskId = 10 + i

                // ЛОГИКА ПРОГРЕССА (как в ReadingScreen):
                // Ищем записи конкретно для этого типа задач
                val taskHistory = userProgress.filter { it.taskId == dbTaskId }
                val countSolved = taskHistory.size

                // Лучший результат
                val bestScore = if (countSolved > 0) taskHistory.maxOf { it.score } else 0
                val maxPossible = if (countSolved > 0) taskHistory.first().totalQuestions else 0

                ListeningTaskCard(
                    number = i, // Отображаем "Tarea 1", а не "Tarea 11"
                    title = getListeningTaskTitle(i),
                    solvedCount = countSolved,
                    bestScoreText = if (countSolved > 0) "$bestScore/$maxPossible (Récord)" else "",
                    onClick = { onTaskSelected(dbTaskId) } // Передаем ID 11 для загрузки
                )
            }
        }
    }
}

// --- ВСПОМОГАТЕЛЬНЫЕ КОМПОНЕНТЫ ДЛЯ АУДИРОВАНИЯ ---
// (Они копируют логику Reading, но используют свои цвета)

@Composable
fun ListeningStatItem(icon: String, label: String, value: String, valueColor: String = "#2c3e50") {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            minWidth(100.px)
        }
    }) {
        Div({ style { fontSize(2.cssRem); marginBottom(5.px) } }) { Text(icon) }
        Span({ style { color(Color("#7f8c8d")); fontSize(0.9.cssRem); marginBottom(5.px) } }) { Text(label) }
        Span({ style { color(Color(valueColor)); fontSize(1.2.cssRem); fontWeight("bold") } }) { Text(value) }
    }
}

@Composable
fun ListeningTaskCard(
    number: Int,
    title: String,
    solvedCount: Int,
    bestScoreText: String,
    onClick: () -> Unit
) {
    val isSolvedAtLeastOnce = solvedCount > 0

    Div({
        onClick { onClick() }
        style {
            backgroundColor(Color.white)
            padding(20.px)
            borderRadius(12.px)
            property("box-shadow", "0 4px 6px rgba(0,0,0,0.05)")
            cursor("pointer")
            property("transition", "transform 0.2s")
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            // Оранжевая полоска слева (стиль Аудирования)
            property("border-left", "5px solid ${getListeningTaskColor(number)}")
        }
        attr("onmouseover", "this.style.transform='translateY(-3px)'")
        attr("onmouseout", "this.style.transform='translateY(0)'")
    }) {
        // Левая часть: Название
        Div {
            H3({ style { margin(0.px); marginBottom(5.px); color(Color("#d35400")) } }) {
                Text("Tarea $number")
            }
            Span({ style { color(Color("#7f8c8d")); fontSize(0.9.cssRem) } }) {
                Text(title)
            }

            // ЛОГИКА ОТОБРАЖЕНИЯ ПРОГРЕССА (Как в ReadingScreen)
            if (isSolvedAtLeastOnce) {
                Div({ style { marginTop(10.px); fontSize(0.85.cssRem); color(Color("#27ae60")); display(DisplayStyle.Flex); alignItems(AlignItems.Center) } }) {
                    // Показываем количество решенных сетов
                    Text("✅ $solvedCount sets completados")
                }
            } else {
                Div({ style { marginTop(10.px); fontSize(0.85.cssRem); color(Color("#bdc3c7")) } }) {
                    Text("⚪ Aún no iniciado")
                }
            }
        }

        // Правая часть: Лучший результат или Стрелка
        Div({ style { textAlign("right") } }) {
            if (isSolvedAtLeastOnce) {
                Div({ style { fontSize(0.8.cssRem); color(Color("#7f8c8d")) } }) { Text("Récord:") }
                Span({ style { fontSize(1.1.cssRem); color(Color("#2c3e50")); fontWeight("bold") } }) {
                    Text(bestScoreText)
                }
            } else {
                Div({
                    style {
                        width(35.px); height(35.px); borderRadius(50.percent)
                        backgroundColor(Color("#fef5e7")) // Светло-оранжевый кружок
                        display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); alignItems(AlignItems.Center)
                        color(Color("#f39c12"))
                    }
                }) { Text("▶") }
            }
        }
    }
}

// Названия задач для Аудирования (примерные, по формату SIELE)
fun getListeningTaskTitle(number: Int): String {
    return when (number) {
        1 -> "Conversación sencilla"
        2 -> "Mensajes personales"
        3 -> "Entrevista (Radio)"
        4 -> "Conferencias / Charlas"
        5 -> "Monólogos complejos"
        else -> "Tarea Auditiva"
    }
}

// Цвета для задач Аудирования (Теплая гамма: Желтый -> Красный)
fun getListeningTaskColor(number: Int): String {
    return when (number) {
        1 -> "#f1c40f" // Yellow
        2 -> "#f39c12" // Orange
        3 -> "#e67e22" // Dark Orange
        4 -> "#d35400" // Pumpkin
        5 -> "#c0392b" // Reddish
        else -> "#95a5a6"
    }
}